import { Observable, BehaviorSubject, Subscriber } from "rxjs"
import { filter, first } from "rxjs/operators"
import ApiUrl from "./ApiUrl"
import BuildFetchRequest, { RequestOptions } from "./BuildFetchRequest"
import rootStore from "../stores/RootStore"
import VerificationResponse from "./models/VerificationResponse"

enum HttpStatusCode {
  UNAUTHORIZED = 401,
  CONFLICT = 409,
}

interface MetaData {
  [key: string]: string | number
}
export class APIError extends Error {
  status: number
  data: MetaData

  constructor(message: string, status: number, data: MetaData) {
    super(message);
    this.name = "APIError"
    this.status = status
    this.data = data || {}
  }
}

class RequestLockService {
  locked = false
  subject = new BehaviorSubject<boolean | null>(null)

  lock = () => {
    this.locked = true
    this.subject.next(null)
  }

  isLocked = () => this.locked

  unlock = (successfull: boolean) => {
    this.locked = false
    this.subject.next(successfull)
  }

  waitSubject = (): Observable<boolean | null> => this.subject.pipe(
    filter(value => value !== null),
    first()
  )
}

const lockService = new RequestLockService()

function fetchRequestToken(onError: () => void) {
  const authStore = rootStore.authStore
  const token = authStore.authToken
  return fetch(ApiUrl() + `Authenticate/RefreshToken?token=${token}`, BuildFetchRequest({ method: 'get', withCredentials: true }))
    .then(async (response: Response) => {
      if (response.ok) {
        const data = await response.text();
        return data ? JSON.parse(data) : {};
      }
      throw new APIError(response.statusText, response.status, {})
    })
    .then((result: VerificationResponse) => {
      if (rootStore.authStore.authToken) {
        rootStore.authStore.setAuthToken(result.authToken)
      }
      lockService.unlock(true)
    })
    .catch(() => {
      lockService.unlock(false)
      onError()
    })
}

function refetchInitialRequest<T>(observer: Subscriber<T>, url: string, body?: string, method?: string, options?: RequestOptions) {
  return ObservableFromFetch<T>(url, body, method, { ...options, retry: true }).subscribe({
    next: (result: T) => {
      observer.next(result)
      observer.complete()
    },
    error: (err) => observer.error(err)
  })
}

export function ObservableFromFetch<T>(url: string, body?: string, method?: string, options?: RequestOptions): Observable<T> {
  return new Observable<T>((observer: Subscriber<T>) => {
    const controller = options?.abortController ?? new AbortController()

    const init = BuildFetchRequest({ body, method, withCredentials: options?.withCredentials, abortController: controller })

    fetch(ApiUrl() + url, init)
      .then((response: Response) => {
        if (response.ok) {
          return response.text()
            .then(data => data ? JSON.parse(data) : {})
        } else {
          return response.text()
            .then(data => data ? JSON.parse(data) : {})
            .then(json => {
              throw new APIError(json.message ?? response.statusText, response.status, json.data)
            })
        }
      })
      .then((result: T) => {
        observer.next(result)
        observer.complete()
      })
      .catch(err => {
        if (err.status !== HttpStatusCode.UNAUTHORIZED) {
          observer.error(err)
          return
        }

        const logout = () => {
          observer.error(err)
          rootStore.authStore.logout()
        }

        if (rootStore.authStore.authToken && !options?.retry) {
          if (!lockService.isLocked()) {
            lockService.lock()
            fetchRequestToken(logout).then(() => {
              refetchInitialRequest(observer, url, body, method, { withCredentials: options?.withCredentials, abortController: controller })
            })
          }
          else {
            lockService.waitSubject().subscribe({
              next: (successfull: boolean | null) => {
                if (successfull) {
                  refetchInitialRequest(observer, url, body, method, { withCredentials: options?.withCredentials, abortController: controller })
                }
              }
            })
          }
        } else {
          logout()
        }
      })

    return () => {
      controller.abort()
    }
  })
}
